import { computePosition, offset, flip, shift, autoUpdate, type Placement } from '@floating-ui/vue';
import type { Directive, DirectiveBinding } from 'vue';

/**
 * `v-tooltip` — a lightweight, accessible tooltip directive built on Floating UI.
 * The tooltip is appended to <body> and positioned with computePosition, so it is
 * never clipped by an `overflow` ancestor (unlike the daisyUI CSS tooltip). It
 * shows on hover AND keyboard focus, and wires `aria-describedby` while visible.
 *
 * Usage: `v-tooltip="'Enhance with AI'"` or `v-tooltip:right="label"`.
 * Keep an `aria-label` on icon-only buttons — the tooltip is a visual affordance,
 * not the accessible name.
 *
 * Modifier: `v-tooltip.truncate="fullText"` only shows the tooltip when the
 * element's content is actually clipped (e.g. a `.truncate` label whose text
 * overflows). On short, fully-visible text it stays silent — no redundant
 * tooltip, and no mis-anchored bubble over a wide-but-mostly-empty flex cell.
 */
interface TooltipState {
    label: string;
    placement: Placement;
    truncateOnly: boolean;
    tooltipEl: HTMLElement | null;
    cleanupAutoUpdate: (() => void) | null;
    id: string;
    onEnter: () => void;
    onLeave: () => void;
    onFocus: () => void;
}

const stateMap = new WeakMap<HTMLElement, TooltipState>();
let counter = 0;

function createTooltipEl(label: string, id: string): HTMLElement {
    const el = document.createElement('span');
    el.id = id;
    el.setAttribute('role', 'tooltip');
    // `popover` promotes the tooltip into the top layer so it renders above native
    // <dialog> modals (which otherwise cover a body-appended element).
    el.setAttribute('popover', 'manual');
    el.textContent = label;
    el.className =
        'pointer-events-none fixed z-[9999] rounded-md border-0 bg-neutral px-2 py-1 text-xs font-medium text-neutral-content shadow-lg whitespace-nowrap';
    // Neutralize the UA popover box (inset/margin) so Floating UI controls position.
    el.style.margin = '0';
    el.style.inset = 'auto';
    el.style.left = '0';
    el.style.top = '0';
    el.style.width = 'max-content';

    return el;
}

function isOverflowing(el: HTMLElement): boolean {
    return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
}

function showTooltip(el: HTMLElement, state: TooltipState): void {
    if (!state.label || state.tooltipEl) {
        return;
    }
    // `.truncate` mode: only surface the tooltip when the text is actually clipped.
    if (state.truncateOnly && !isOverflowing(el)) {
        return;
    }
    const tip = createTooltipEl(state.label, state.id);
    document.body.appendChild(tip);
    state.tooltipEl = tip;
    el.setAttribute('aria-describedby', state.id);

    // Show in the top layer where supported; otherwise it stays a plain body node.
    try {
        if (typeof tip.showPopover === 'function') {
            tip.showPopover();
        }
    } catch {
        // Ignore — fall back to default (normal-layer) rendering.
    }

    state.cleanupAutoUpdate = autoUpdate(el, tip, () => {
        void computePosition(el, tip, {
            placement: state.placement,
            strategy: 'fixed',
            middleware: [offset(6), flip(), shift({ padding: 6 })],
        }).then(({ x, y }) => {
            tip.style.left = `${x}px`;
            tip.style.top = `${y}px`;
        });
    });
}

function hideTooltip(el: HTMLElement, state: TooltipState): void {
    state.cleanupAutoUpdate?.();
    state.cleanupAutoUpdate = null;
    state.tooltipEl?.remove();
    state.tooltipEl = null;
    el.removeAttribute('aria-describedby');
}

function setup(el: HTMLElement, binding: DirectiveBinding<string | undefined>): void {
    const state: TooltipState = {
        label: binding.value ?? '',
        placement: (binding.arg as Placement | undefined) ?? 'top',
        truncateOnly: binding.modifiers.truncate === true,
        tooltipEl: null,
        cleanupAutoUpdate: null,
        id: `tt-${++counter}`,
        onEnter: () => showTooltip(el, state),
        onLeave: () => hideTooltip(el, state),
        // Only surface on *keyboard* focus. Focus that is programmatically
        // restored after a mouse interaction (e.g. a modal closing and returning
        // focus to its trigger) is not `:focus-visible`, so the tooltip won't
        // linger over a button the user is no longer pointing at.
        onFocus: () => {
            let keyboard: boolean;
            try {
                keyboard = el.matches(':focus-visible');
            } catch {
                keyboard = true; // Older engines: fall back to the prior behaviour.
            }
            if (keyboard) {
                showTooltip(el, state);
            }
        },
    };
    stateMap.set(el, state);
    el.addEventListener('mouseenter', state.onEnter);
    el.addEventListener('mouseleave', state.onLeave);
    el.addEventListener('focusin', state.onFocus);
    el.addEventListener('focusout', state.onLeave);
}

function teardown(el: HTMLElement): void {
    const state = stateMap.get(el);
    if (!state) {
        return;
    }
    el.removeEventListener('mouseenter', state.onEnter);
    el.removeEventListener('mouseleave', state.onLeave);
    el.removeEventListener('focusin', state.onFocus);
    el.removeEventListener('focusout', state.onLeave);
    hideTooltip(el, state);
    stateMap.delete(el);
}

export const tooltip: Directive<HTMLElement, string | undefined> = {
    mounted(el, binding) {
        setup(el, binding);
    },
    updated(el, binding) {
        const state = stateMap.get(el);
        if (!state) {
            return;
        }
        state.label = binding.value ?? '';
        state.placement = (binding.arg as Placement | undefined) ?? 'top';
        if (state.tooltipEl) {
            state.tooltipEl.textContent = state.label;
        }
    },
    unmounted(el) {
        teardown(el);
    },
};
